// @flow
import * as React from 'react';

import get from 'lodash/get';
import { Link } from 'react-router-dom';

import { selectCartUUID, type DecoratedProduct } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Hide, Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import { ListRow } from '../../ListRow';

import ProductItem from './components/ProductItem';

import styles from './CloudAdd.scss';

type Props = { product: DecoratedProduct };

export function CloudAdd({ product }: Props): React.MixedElement {
  const { __, formatDate } = useTranslator();

  /// XXX: we may want to use some helper from @gandi/cart
  const translations = {
    cores: (count: number) => __('%(count)s CPU cores', { count }),
    ipv4: (count: number) =>
      count > 0 ? __('%(count)s IP v4', { count }) : '',
    ipv6: (count: number) =>
      count > 0 ? __('%(count)s IP v6', { count }) : '',
    memory: (count: number) =>
      count >= 1024
        ? __('%(count)s GB of RAM', { count: count / 1024 })
        : __('%(count)s MB of RAM', { count }),
    storage: (count: number) => __('%(count)s GB of storage', { count }),
  };

  const cartId = useAppSelector(selectCartUUID);
  const isServer = !!product.payload.server;

  if (isServer && product.payload.isFirstServer) {
    /* Legacy style */
    const serverDetails = (
      <Hide until="xsm">
        <div className={styles.specifications}>
          {['cores', 'ipv6', 'ipv4', 'memory', 'storage']
            .filter(key => !!translations[key])
            .filter(
              key => !!translations[key](product.payload.server?.[key] || 0),
            )
            .map(key => (
              <Text
                color="grey-darker"
                key={`${key}-${product.id}`}
                size="small"
                tagName="div"
                weight="semibold"
              >
                {translations[key](product.payload.server?.[key] || 0)}
              </Text>
            ))}
        </div>
      </Hide>
    );

    const fallBackLabel = product.payload.server?.image
      ? // $FlowFixMe[incompatible-type]
        `${product.label} - ${product.payload.server.image}`
      : product.label;

    const label = product.payload.server?.hostname || fallBackLabel || '';

    const cloudOptions = [
      ...['cores', 'ipv6', 'ipv4', 'memory', 'storage']
        .filter(key => !!translations[key])
        .filter(key => !!translations[key](product.payload.server?.[key] || 0))
        .map(key => translations[key](product.payload.server?.[key] || 0)),
    ];

    return (
      <ProductItem
        errors={Array.isArray(product.errors) ? product.errors : undefined}
        hasTaxes={product.hasTaxes}
        key={`${product.id}-server-row`}
        priceBeforeTaxes={
          product.isWorking
            ? undefined
            : get(product.total, 'before_discount') ||
              get(product.total, 'before_taxes')
        }
        productId={product.id}
        label={label}
        detail={serverDetails}
        listRow={<ListRow intent="muted" options={cloudOptions} />}
      >
        <Link to={`/cloud/create/server/${cartId}/${product.id}`}>
          {__('Change configuration')}
        </Link>
      </ProductItem>
    );
  }

  /* Legacy style */
  const creditDetails = (
    <div>
      {product.payload.dateExhaust && (
        <Text color="light" size="small" weight="semibold">
          {__('Runs out %(date)s', {
            date: formatDate(product.payload.dateExhaust, 'LLL'),
          })}
        </Text>
      )}
      <Hide until="xsm">
        <div className={styles.specifications}>
          {isServer && (
            <Text color="light" size="small" tagName="div" weight="semibold">
              {__('1 server')}
            </Text>
          )}
          <Text color="light" size="small" tagName="div" weight="semibold">
            {__('Renewal Date Extension')}
          </Text>
        </div>
      </Hide>
    </div>
  );

  const label = __('Funds added to Cloud Account');
  const detail = creditDetails;

  const cloudOptions = [
    product.payload.dateExhaust
      ? __('Runs out %(date)s', {
          date: formatDate(product.payload.dateExhaust, 'LLL'),
        })
      : null,

    isServer ? __('1 server') : null,
    __('Renewal Date Extension'),
  ];

  return (
    <ProductItem
      errors={Array.isArray(product.errors) ? product.errors : undefined}
      hasTaxes={product.hasTaxes}
      key={`${product.id}-credit-row`}
      priceBeforeTaxes={
        product.isWorking ? undefined : product.total?.before_taxes
      }
      productId={product.id}
      label={label}
      detail={detail}
      listRow={<ListRow intent="muted" options={cloudOptions} />}
    >
      {isServer && (
        <Link to={`/cloud/create/server/${cartId}/${product.id}`}>
          {__('Change configuration')}
        </Link>
      )}
    </ProductItem>
  );
}
